import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { auth0SilentTokenOptions } from '~/lib/constants/global.constants';
import { VITE_MAI_WS_GATEWAY } from '~/lib/constants/env';
import { isInvalidRefreshTokenError } from '~/lib/util/auth.util';
import { security } from '~/store/security';

import {
  ClientFrame,
  DeltaFrame,
  DoneFrame,
  ErrorFrame,
  parseServerFrame,
  ToolCallFrame,
  ToolResultFrame,
} from './maiSocket.frames';

export interface MaiSocketCallbacks {
  onDelta: (frame: DeltaFrame) => void;
  onToolCall: (frame: ToolCallFrame) => void;
  onToolResult: (frame: ToolResultFrame) => void;
  onDone: (frame: DoneFrame) => void;
  onError: (frame: ErrorFrame) => void;
  // Transport failure (connection dropped mid-turn / terminal auth) — distinct
  // from onError, which is an agent error frame over a live connection.
  onConnectionError?: () => void;
  // Fires on every inbound frame (including ping) so the consumer can run a
  // stream-inactivity watchdog — a silent reconnect orphans the server-side
  // stream, so a live turn that goes quiet needs a timeout, not an infinite wait.
  onActivity?: () => void;
}

export interface MaiSocket {
  send: (frame: ClientFrame) => void;
  ensureConnected: () => void;
  resetConnection: () => void;
}

const NORMAL_CLOSURE = 1000;

// Fetches a fresh Auth0 token and returns the authenticated gateway URL. The
// token is presented as a query-string param because a browser WebSocket can't
// set an Authorization header on the upgrade request; it must be the raw JWT
// (no "Bearer " prefix) since the $connect authorizer splits on "." expecting
// exactly three segments.
const buildAuthenticatedUrl = async (): Promise<string> => {
  const token = await security.getAccessTokenSilently()(
    auth0SilentTokenOptions,
  );
  return `${VITE_MAI_WS_GATEWAY}?token=${encodeURIComponent(token)}`;
};

/**
 * Manages the Mai streaming WebSocket lifecycle: fresh-token authenticated
 * connect, frame parsing/routing, pre-open send buffering, ping keep-alive
 * handling, and clean teardown on unmount. An abnormal close is surfaced via
 * onConnectionError rather than reconnected: the gateway cannot resume an
 * aborted generation, and reconnecting an idle socket loops forever against
 * its idle timeout.
 *
 * Never logs the socket url, the token, or frame bodies — all carry PHI.
 */
export const useMaiSocket = (callbacks: MaiSocketCallbacks): MaiSocket => {
  const socketRef = useRef<WebSocket | null>(null);
  const bufferRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);
  const generationRef = useRef(0);

  // Callbacks are read through a ref refreshed each render so the socket's
  // long-lived message handler never captures a stale closure.
  const callbacksRef = useRef(callbacks);
  useLayoutEffect(() => {
    callbacksRef.current = callbacks;
  });

  const flushBuffer = useCallback(() => {
    const socket = socketRef.current;
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      bufferRef.current !== null
    ) {
      socket.send(bufferRef.current);
      bufferRef.current = null;
    }
  }, []);

  const routeFrame = useCallback((raw: string) => {
    const frame = parseServerFrame(raw);
    if (!frame) return;
    const cb = callbacksRef.current;
    cb.onActivity?.();
    switch (frame.type) {
      case 'delta':
        cb.onDelta(frame);
        break;
      case 'tool_call':
        cb.onToolCall(frame);
        break;
      case 'tool_result':
        cb.onToolResult(frame);
        break;
      case 'done':
        cb.onDone(frame);
        break;
      case 'error':
        cb.onError(frame);
        break;
      case 'ping':
        // Keep-alive only; intentionally ignored so it triggers no re-render.
        break;
    }
  }, []);

  const connect = useCallback(async () => {
    const current = socketRef.current;
    if (
      current &&
      (current.readyState === WebSocket.CONNECTING ||
        current.readyState === WebSocket.OPEN)
    ) {
      return;
    }
    if (connectingRef.current) return;
    connectingRef.current = true;
    const generation = generationRef.current;

    let url: string;
    try {
      url = await buildAuthenticatedUrl();
    } catch (error) {
      if (generation !== generationRef.current) return;
      connectingRef.current = false;
      // The buffered frame belongs to the turn this connect was opening for. A
      // later ensureConnected() would flush it, putting a question the provider
      // already saw fail back on the wire.
      bufferRef.current = null;
      // A terminal refresh-token error means the session is unrecoverable; the
      // security singleton already triggers force-logout, so stop retrying.
      if (isInvalidRefreshTokenError(error) && mountedRef.current) {
        callbacksRef.current.onConnectionError?.();
      }
      return;
    }
    if (generation !== generationRef.current) return;
    connectingRef.current = false;
    if (!mountedRef.current) return;

    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onopen = () => {
      flushBuffer();
    };
    socket.onmessage = (event: MessageEvent) => {
      if (!mountedRef.current) return;
      routeFrame(event.data as string);
    };
    socket.onclose = (event: CloseEvent) => {
      if (!mountedRef.current) return;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      // A close event can be delivered after send() already replaced this
      // socket: only the socket still owned may clear the refs or fail a turn.
      if (socketRef.current !== socket) return;
      socketRef.current = null;
      // Drop any unflushed frame: it belongs to a turn this connection can no
      // longer serve, and flushing it on the next connect would put a question
      // the provider already gave up on back on the wire.
      bufferRef.current = null;
      if (event.code === NORMAL_CLOSURE) return;
      // The gateway aborts the generation on $disconnect and serves one
      // generation per connectionId, so a reconnect could never deliver the
      // turn that was in flight: fail it now rather than at the watchdog. With
      // no turn in flight, stay closed — send() reconnects on demand, so
      // reconnecting here would re-handshake forever against the gateway's
      // idle timeout.
      callbacksRef.current.onConnectionError?.();
    };
    socket.onerror = () => {
      // Swallowed: the close handler surfaces the failure, and error events
      // carry no actionable detail we're allowed to log.
    };
  }, [flushBuffer, routeFrame]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      const socket = socketRef.current;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        if (
          socket.readyState === WebSocket.CONNECTING ||
          socket.readyState === WebSocket.OPEN
        ) {
          socket.close(NORMAL_CLOSURE);
        }
      }
      socketRef.current = null;
      bufferRef.current = null;
      connectingRef.current = false;
    };
  }, []);

  const ensureConnected = useCallback(() => {
    void connect();
  }, [connect]);

  const resetConnection = useCallback(() => {
    generationRef.current += 1;
    const socket = socketRef.current;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      if (
        socket.readyState === WebSocket.CONNECTING ||
        socket.readyState === WebSocket.OPEN
      ) {
        socket.close(NORMAL_CLOSURE);
      }
    }
    socketRef.current = null;
    bufferRef.current = null;
    connectingRef.current = false;
  }, []);

  const send = useCallback(
    (frame: ClientFrame) => {
      const serialized = JSON.stringify(frame);
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(serialized);
        return;
      }
      // Not open: buffer to flush on open, and reconnect on demand if the
      // socket is gone (an idle close leaves it closed by design).
      bufferRef.current = serialized;
      const isClosed =
        !socket ||
        socket.readyState === WebSocket.CLOSING ||
        socket.readyState === WebSocket.CLOSED;
      if (isClosed) void connect();
    },
    [connect],
  );

  return { send, ensureConnected, resetConnection };
};
